import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { usePeriod } from '@/app/PeriodContext';
import { PeriodBadge } from '@/app/PeriodBadge';
import { formatMoney, formatDate, isNegative } from '@/lib/money';
import type { MoneyString } from '@/lib/money';
import { Modal, Field } from '@/features/transactions/TransactionForm';
import type { ReferenceData, Transaction } from '@/features/transactions/types';
import { SharingGroupForm } from './SharingGroupForm';

type Member = {
  member_entity_id: number;
  name: string | null;
  color: string | null;
  default_share_percentage: number;
  status: string;
};

type Group = {
  id: number;
  name: string;
  split_mode: string;
  settlement_frequency: string;
  entity_id: number | null;
  members: Member[];
  open_settlements: number;
};

type Participant = {
  entity_id: number;
  name: string;
  color: string | null;
  paid: MoneyString;
  owed: MoneyString;
  balance: MoneyString;
  position: 'a_receber' | 'a_pagar' | 'quite';
  expenses_paid_count: number;
  /** Cota padrão do grupo — RN-51. Vem junto para a tela não precisar cruzar. */
  default_share_percentage: number;
};

type SuggestedTransfer = {
  from_entity_id: number;
  from_name: string;
  to_entity_id: number;
  to_name: string;
  amount: MoneyString;
  label: string;
};

type Balance = {
  group: { id: number; name: string; split_mode: string; settlement_frequency: string };
  period: { from: string; to: string };
  participants: Participant[];
  totals: { shared_expenses: MoneyString; transactions: number; balance_sum: MoneyString };
  expenses: Array<{
    transaction_id: number;
    description: string;
    competence_date: string;
    amount: MoneyString;
    paid_by_entity_id: number | null;
    shares: Array<{ member_entity_id: number; share_amount: MoneyString }>;
  }>;
  suggested_transfers: SuggestedTransfer[];
};

type Settlement = {
  id: number;
  period_start: string;
  period_end: string;
  from_entity_id: number;
  from_name: string | null;
  to_entity_id: number;
  to_name: string | null;
  amount: MoneyString;
  status: string;
  transfer_id: number | null;
  settled_at: string | null;
};

export function SharingPage({ selectedIds }: { selectedIds: number[] }) {
  const [groupId, setGroupId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [adding, setAdding] = useState(false);
  const [paying, setPaying] = useState<Settlement | null>(null);
  const queryClient = useQueryClient();

  const groups = useQuery({
    queryKey: ['sharing-groups', selectedIds],
    queryFn: () => api.get<{ data: Group[] }>('/sharing-groups', { entityIds: selectedIds }),
    select: (r) => r.data,
  });

  const reference = useQuery({
    queryKey: ['reference', selectedIds],
    queryFn: () => api.get<{ data: ReferenceData }>('/reference', { entityIds: selectedIds }),
    select: (r) => r.data,
    staleTime: 5 * 60 * 1000,
  });

  const list = groups.data ?? [];
  const active = groupId ?? list[0]?.id ?? null;

  function refresh() {
    setCreating(false);
    setAdding(false);
    setPaying(null);
    void queryClient.invalidateQueries({ queryKey: ['sharing-groups'] });
    void queryClient.invalidateQueries({ queryKey: ['sharing-balance'] });
    void queryClient.invalidateQueries({ queryKey: ['sharing-settlements'] });
    void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['accounts'] });
  }

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Despesas compartilhadas</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Quem pagou o quê, quanto cada um devia e quem deve a quem.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          disabled={!reference.data}
          className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
        >
          + Novo grupo
        </button>
      </div>

      {groups.isPending ? (
        <div className="mt-5 h-40 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
      ) : list.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-slate-300 p-10 text-center dark:border-slate-700">
          <p className="text-slate-500 dark:text-slate-400">Nenhum grupo de compartilhamento.</p>
          <p className="mx-auto mt-1 max-w-lg text-sm text-slate-400">
            Um grupo registra quem divide despesas com quem. A despesa continua inteira na entidade
            de quem pagou — o grupo só guarda quanto cada um devia, sem duplicar o gasto.
          </p>
          <button
            onClick={() => setCreating(true)}
            disabled={!reference.data}
            className="mt-3 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            Criar o primeiro grupo
          </button>
        </div>
      ) : (
        <>
          {list.length > 1 && (
            <div className="mt-4 flex flex-wrap gap-1">
              {list.map((g) => (
                <button
                  key={g.id}
                  onClick={() => setGroupId(g.id)}
                  className={`rounded-lg px-3 py-1.5 text-sm transition ${
                    active === g.id
                      ? 'bg-indigo-600 text-white'
                      : 'border border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-300'
                  }`}
                >
                  {g.name}
                </button>
              ))}
            </div>
          )}

          {active !== null && (
            <GroupPanel
              group={list.find((g) => g.id === active)!}
              selectedIds={selectedIds}
              onAddExpense={() => setAdding(true)}
              onPay={setPaying}
              onChanged={refresh}
            />
          )}
        </>
      )}

      {creating && reference.data && (
        <SharingGroupForm
          reference={reference.data}
          selectedIds={selectedIds}
          onClose={() => setCreating(false)}
          onSaved={refresh}
        />
      )}

      {adding && active !== null && (
        <AddExpenseDialog
          groupId={active}
          selectedIds={selectedIds}
          onClose={() => setAdding(false)}
          onShared={refresh}
        />
      )}

      {paying && reference.data && (
        <PaySettlementDialog
          settlement={paying}
          reference={reference.data}
          selectedIds={selectedIds}
          onClose={() => setPaying(null)}
          onPaid={refresh}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------

function GroupPanel({
  group, selectedIds, onAddExpense, onPay, onChanged,
}: {
  group: Group;
  selectedIds: number[];
  onAddExpense: () => void;
  onPay: (s: Settlement) => void;
  onChanged: () => void;
}) {
  const { period } = usePeriod();

  const balance = useQuery({
    queryKey: ['sharing-balance', group.id, period.from, period.to, selectedIds],
    queryFn: () =>
      api.get<{ data: Balance }>(
        `/sharing-groups/${group.id}/balance?from=${period.from}&to=${period.to}`,
        { entityIds: selectedIds },
      ),
    select: (r) => r.data,
  });

  const settlements = useQuery({
    queryKey: ['sharing-settlements', group.id, selectedIds],
    queryFn: () =>
      api.get<{ data: Settlement[] }>(`/sharing-groups/${group.id}/settlements`, {
        entityIds: selectedIds,
      }),
    select: (r) => r.data,
  });

  const close = useMutation({
    mutationFn: () =>
      api.post<{ data: { note: string } }>(
        `/sharing-groups/${group.id}/settle`,
        { from: period.from, to: period.to },
        { entityIds: selectedIds },
      ),
    onSuccess: (res) => { window.alert(res.data.note); onChanged(); },
    onError: (e) => window.alert(e instanceof ApiError ? e.message : 'Erro ao fechar o acerto.'),
  });

  const unshare = useMutation({
    mutationFn: (transactionId: number) =>
      api.delete(`/transactions/${transactionId}/share`, { entityIds: selectedIds }),
    onSuccess: onChanged,
    onError: (e) => window.alert(e instanceof ApiError ? e.message : 'Erro.'),
  });

  if (balance.isPending) {
    return <div className="mt-5 h-64 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />;
  }

  if (balance.isError) {
    const error = balance.error instanceof ApiError ? balance.error : null;

    return (
      <p className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
        {error?.message ?? 'Não foi possível calcular o acerto.'}
      </p>
    );
  }

  const data = balance.data;
  const open = (settlements.data ?? []).filter((s) => s.status === 'aberto');

  return (
    <div className="mt-5 space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <PeriodBadge />
        <button
          onClick={onAddExpense}
          className="ml-auto mb-2 rounded-lg border border-slate-300 px-3 py-1.5 text-sm transition hover:border-indigo-500 hover:text-indigo-600 dark:border-slate-700"
        >
          + Compartilhar despesa
        </button>
      </div>

      {/* RF-CAS-03 — quem tem a receber, quem tem a pagar. */}
      <div className="grid gap-3 sm:grid-cols-2">
        {data.participants.map((p) => (
          <div
            key={p.entity_id}
            className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ background: p.color ?? '#94a3b8' }}
              />
              <p className="font-medium">{p.name}</p>
              <span className="ml-auto text-xs text-slate-400">
                {p.default_share_percentage.toFixed(0).replace('.', ',')}%
              </span>
            </div>

            <p
              className={`mt-2 text-lg font-semibold ${
                p.position === 'a_pagar'
                  ? 'text-rose-600 dark:text-rose-400'
                  : p.position === 'a_receber'
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : ''
              }`}
            >
              {formatMoney(isNegative(p.balance) ? p.balance.replace('-', '') : p.balance)}
              <span className="ml-1.5 text-xs font-normal text-slate-500 dark:text-slate-400">
                {p.position === 'a_pagar' ? 'a pagar' : p.position === 'a_receber' ? 'a receber' : 'quite'}
              </span>
            </p>

            <dl className="mt-2 space-y-0.5 text-xs text-slate-500 dark:text-slate-400">
              <div className="flex justify-between">
                <dt>Pagou</dt>
                <dd>{formatMoney(p.paid)}</dd>
              </div>
              <div className="flex justify-between">
                <dt>Devia</dt>
                <dd>{formatMoney(p.owed)}</dd>
              </div>
            </dl>
          </div>
        ))}
      </div>

      {/* A identidade exposta: quem lê pode conferir sem ter de confiar. */}
      <p className="text-[11px] text-slate-400">
        Total compartilhado no período: {formatMoney(data.totals.shared_expenses)} em{' '}
        {data.totals.transactions} lançamento(s). A soma dos saldos é{' '}
        {formatMoney(data.totals.balance_sum)} — ela é sempre zero, porque o total pago e o total
        devido são o mesmo dinheiro visto de dois ângulos.
      </p>

      {data.suggested_transfers.length > 0 && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-900/50 dark:bg-indigo-950/30">
          <p className="text-sm font-medium text-indigo-900 dark:text-indigo-200">
            Para zerar o período
          </p>
          <ul className="mt-1.5 space-y-1 text-sm text-indigo-800 dark:text-indigo-300">
            {data.suggested_transfers.map((t, i) => <li key={i}>· {t.label}</li>)}
          </ul>
          <p className="mt-2 text-xs text-indigo-800/70 dark:text-indigo-300/70">
            Fechar o acerto congela estes valores. Nenhuma despesa nova é criada — o gasto já foi
            reconhecido na entidade de quem pagou.
          </p>
          <button
            onClick={() => {
              if (window.confirm(
                'Fechar o acerto deste período?\n\n' +
                'As despesas do período saem do saldo em aberto e os valores ficam congelados. ' +
                'Nenhuma receita ou despesa é criada.',
              )) {
                close.mutate();
              }
            }}
            disabled={close.isPending}
            className="mt-3 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            {close.isPending ? 'Fechando…' : 'Fechar acerto do período'}
          </button>
        </div>
      )}

      {open.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/30">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
            Acerto fechado, aguardando pagamento
          </p>
          <div className="mt-2 space-y-1.5">
            {open.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span>
                  {s.from_name} paga <strong>{formatMoney(s.amount)}</strong> a {s.to_name}
                </span>
                <span className="text-xs text-amber-800/70 dark:text-amber-300/70">
                  {formatDate(s.period_start)} a {formatDate(s.period_end)}
                </span>
                <button
                  onClick={() => onPay(s)}
                  className="ml-auto rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-emerald-700"
                >
                  Registrar pagamento
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* RF-CAS-05 — quem pagou o quê. */}
      {data.expenses.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <p className="border-b border-slate-200 px-3.5 py-2.5 font-medium dark:border-slate-800">
            Despesas compartilhadas do período
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-3 py-2 font-medium">Data</th>
                  <th className="px-3 py-2 font-medium">Descrição</th>
                  <th className="px-3 py-2 font-medium">Quem pagou</th>
                  <th className="px-3 py-2 text-right font-medium">Valor</th>
                  <th className="px-3 py-2 text-right font-medium">Cotas</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {data.expenses.map((e) => (
                  <tr key={e.transaction_id}>
                    <td className="whitespace-nowrap px-3 py-2">{formatDate(e.competence_date)}</td>
                    <td className="px-3 py-2">{e.description}</td>
                    <td className="px-3 py-2 text-slate-500 dark:text-slate-400">
                      {data.participants.find((p) => p.entity_id === e.paid_by_entity_id)?.name ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-medium">
                      {formatMoney(e.amount)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-xs text-slate-500 dark:text-slate-400">
                      {e.shares.map((s) => formatMoney(s.share_amount)).join(' / ')}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => {
                          if (window.confirm('Desfazer o compartilhamento deste lançamento?')) {
                            unshare.mutate(e.transaction_id);
                          }
                        }}
                        className="text-[11px] text-rose-600 hover:underline dark:text-rose-400"
                      >
                        remover
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------

function AddExpenseDialog({
  groupId, selectedIds, onClose, onShared,
}: {
  groupId: number;
  selectedIds: number[];
  onClose: () => void;
  onShared: () => void;
}) {
  const { period } = usePeriod();
  const [picked, setPicked] = useState<number[]>([]);

  const transactions = useQuery({
    queryKey: ['transactions', 'shareable', period.from, period.to, selectedIds],
    queryFn: () =>
      api.get<{ data: Transaction[] }>(
        `/transactions?type=despesa&date_field=competence_date&from=${period.from}&to=${period.to}&per_page=200`,
        { entityIds: selectedIds },
      ),
    select: (r) => r.data,
  });

  const share = useMutation({
    mutationFn: async () => {
      // Uma chamada por lançamento: cada uma valida as cotas por conta própria,
      // e um erro no meio não deixa metade compartilhada em silêncio.
      for (const id of picked) {
        await api.post(`/transactions/${id}/share`, { sharing_group_id: groupId }, {
          entityIds: selectedIds,
        });
      }
    },
    onSuccess: onShared,
    onError: (e) => window.alert(e instanceof ApiError ? e.message : 'Erro ao compartilhar.'),
  });

  const available = (transactions.data ?? []).filter((t) => !t.is_card_purchase || true);

  return (
    <Modal title="Compartilhar despesas" onClose={onClose}>
      <div className="space-y-4">
        <p className="rounded-lg bg-slate-50 px-3.5 py-2.5 text-sm text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
          O lançamento continua inteiro na entidade de quem pagou. Compartilhar registra quanto cada
          participante devia — sem criar lançamento novo e sem mexer no extrato.
        </p>

        {transactions.isPending ? (
          <div className="h-40 animate-pulse rounded-lg bg-slate-200 dark:bg-slate-800" />
        ) : available.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
            Nenhuma despesa no período selecionado.
          </p>
        ) : (
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {available.map((t) => (
              <label
                key={t.id}
                className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-800"
              >
                <input
                  type="checkbox"
                  checked={picked.includes(t.id)}
                  onChange={(e) =>
                    setPicked((prev) =>
                      e.target.checked ? [...prev, t.id] : prev.filter((id) => id !== t.id))
                  }
                />
                <span className="min-w-0 flex-1 truncate">
                  {t.description}
                  <span className="ml-1.5 text-xs text-slate-400">
                    {formatDate(t.competence_date)} · {t.entity_name}
                  </span>
                </span>
                <span className="font-medium">{formatMoney(t.net_amount)}</span>
              </label>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            onClick={() => share.mutate()}
            disabled={picked.length === 0 || share.isPending}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            {share.isPending ? 'Compartilhando…' : `Compartilhar ${picked.length || ''}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function PaySettlementDialog({
  settlement, reference, selectedIds, onClose, onPaid,
}: {
  settlement: Settlement;
  reference: ReferenceData;
  selectedIds: number[];
  onClose: () => void;
  onPaid: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);

  const fromAccounts = reference.accounts.filter((a) => a.entity_id === settlement.from_entity_id);
  const toAccounts = reference.accounts.filter((a) => a.entity_id === settlement.to_entity_id);

  const [fromAccountId, setFromAccountId] = useState(String(fromAccounts[0]?.id ?? ''));
  const [toAccountId, setToAccountId] = useState(String(toAccounts[0]?.id ?? ''));
  const [paidAt, setPaidAt] = useState(today);

  const pay = useMutation({
    mutationFn: () =>
      api.post<{ data: { note: string } }>(
        `/sharing-settlements/${settlement.id}/pay`,
        {
          from_account_id: Number(fromAccountId),
          to_account_id: Number(toAccountId),
          paid_at: paidAt,
        },
        { entityIds: selectedIds },
      ),
    onSuccess: (res) => { window.alert(res.data.note); onPaid(); },
  });

  const error = pay.error instanceof ApiError ? pay.error : null;

  return (
    <Modal title="Registrar pagamento do acerto" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); pay.mutate(); }} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            {error.message}
          </div>
        )}

        {/* Mesma explicação do pagamento de fatura: é transferência, não gasto. */}
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3.5 py-3 text-sm dark:border-indigo-900/50 dark:bg-indigo-950/30">
          <p className="font-medium text-indigo-900 dark:text-indigo-200">
            Isto é uma transferência, não uma despesa.
          </p>
          <p className="mt-1 text-indigo-800/80 dark:text-indigo-300/80">
            O gasto já foi reconhecido quando cada compra foi lançada. O acerto só move dinheiro de
            uma pessoa para a outra — contá-lo como despesa contaria o mesmo gasto duas vezes.
          </p>
        </div>

        <p className="rounded-lg bg-slate-50 px-3.5 py-2.5 text-sm dark:bg-slate-800/50">
          <strong>{settlement.from_name}</strong> paga{' '}
          <strong>{formatMoney(settlement.amount)}</strong> a{' '}
          <strong>{settlement.to_name}</strong>
        </p>

        <Field label="Sai da conta de quem paga" required>
          <select
            value={fromAccountId}
            onChange={(e) => setFromAccountId(e.target.value)}
            required
            className="input w-full"
          >
            <option value="">Selecione</option>
            {fromAccounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>

        <Field label="Entra na conta de quem recebe" required>
          <select
            value={toAccountId}
            onChange={(e) => setToAccountId(e.target.value)}
            required
            className="input w-full"
          >
            <option value="">Selecione</option>
            {toAccounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>

        <Field label="Data" required>
          <input
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
            required
            className="input w-full"
          />
        </Field>

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={pay.isPending || fromAccountId === '' || toAccountId === ''}
            className="flex-1 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
          >
            {pay.isPending ? 'Registrando…' : 'Confirmar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
